import { ChevronDown, Wrench, X } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { useImmer } from "use-immer";

import {
  IgBorderColors,
  IgCoreBackgroundColors,
  IgCoreTextColors,
} from "@intelligems/ig-design-tokens";
import {
  IgButton,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";

import { gateSpec, type WorkspaceGatesConfig } from "../../domainConfig";
import type { WorkspaceGateResult } from "../../types";
import {
  attemptsPhrase,
  describeGateDetail,
  GATE_STATUS_STYLE,
  GATE_TONE_COLORS,
  type GateFailureGroup,
  gateLabel,
  type GateRunSummary,
  groupGateFailures,
  resolveGateStyle,
  resolveStripTone,
  summarizeGates,
} from "./gateStatus";

/** Module-level so the default prop is referentially stable across renders. */
const EMPTY_PRIOR_ATTEMPTS: { attempt: number; gates: never[] }[] = [];

export interface GateProgressStripProps<GateKey extends string> {
  /** Ordered gate rows (length === the domain's gate order). */
  gates: WorkspaceGateResult<GateKey>[];
  /** Supplies the ordered labels, the sentinel, and the detail formatters. */
  config: WorkspaceGatesConfig<GateKey>;
  /** Heading for the region, e.g. "Publish gates". */
  heading: string;
  /** `true` while a publish job is in flight. */
  isPublishing: boolean;
  /** Horizontal inset from the containing workspace surface. */
  horizontalMargin?: number;
  /** Space above the strip within the containing workspace surface. */
  topMargin?: number;
  /** Space below the strip within the containing workspace surface. */
  bottomMargin?: number;
  /**
   * Present while a repair loop owns this run: attempt counters for the
   * "Atlas is fixing this" headline. Null/omitted for domains without a
   * repair loop — the strip then behaves exactly as before.
   */
  repair?: { attempt: number; maxAttempts: number; authorName: string } | null;
  /**
   * Settled attempts this run already burned, oldest first. Rendered as
   * collapsed rows above the live gate list so a refusal the loop has moved
   * past is still named while the run remains unresolved. A successful run
   * clears these failures. Empty for domains with no repair loop.
   */
  priorAttempts?: {
    attempt: number;
    gates: WorkspaceGateResult<GateKey>[];
  }[];
  /**
   * Set when the entity has reached a terminal state this gate run does not
   * describe — a rule the repair loop gave up on, for example. The strip then
   * reports the run as stopped instead of claiming a verdict ("All 13 checks
   * passed" beside a gave-up rule is the defect this closes). The string is
   * the merchant-facing reason, shown as the headline detail.
   */
  abandonedReason?: string | null;
  /**
   * Set when the tracked run is the publish that shipped what is live rather
   * than a check on a pending change. The passed headline then reports the run
   * as history ("this version is live") instead of inviting a publish that
   * already happened.
   */
  describesLiveVersion?: boolean;
  /** Re-run the current check suite from a failed state. */
  onRunChecksAgain?: () => void;
  /** Prevent a re-run when the current draft cannot be tested. */
  runChecksAgainDisabled?: boolean;
  /** Hand the failed check to the domain's AI authoring surface. */
  onAskAiFix?: () => void;
  /** Click a check row → open that gate in the gates tab. */
  onSelectGate?: (gateKey: GateKey) => void;
  /**
   * Present when this strip is the page's publish surface — the run it reports
   * describes a draft that is not live yet, so the verdict and the act of
   * shipping belong on one bar rather than two. Absent ⇒ the strip is a
   * read-out, which is how every other caller uses it.
   */
  publish?: GateStripPublishAction;
  /** A domain operation can span several check jobs. Its outcome owns the summary. */
  operation?: GateStripOperation;
}

export interface GateStripOperation {
  phase:
    | "draft"
    | "checking"
    | "publishing"
    | "checked"
    | "published"
    | "review"
    | "stopped"
    | "failed";
  detail?: string;
  checksOnly?: boolean;
  /** The decision or recovery content, inside this strip rather than a second alert. */
  content?: ReactNode;
}

const OPERATION_STATUS = {
  draft: { headline: "Draft ready", phase: "idle", style: "pending" },
  checking: { headline: "Checking rule…", phase: "running", style: "running" },
  publishing: { headline: "Publishing…", phase: "running", style: "running" },
  checked: { headline: "Checks passed", phase: "passed", style: "passed" },
  published: { headline: "Published", phase: "passed", style: "passed" },
  review: {
    headline: "Review required",
    phase: "needs_review",
    style: "warning",
  },
  failed: { headline: "Could not publish", phase: "failed", style: "failed" },
  stopped: { headline: "Tests stopped", phase: "abandoned", style: "warning" },
} as const;

export interface GateStripPublishAction {
  onPublish: () => void;
  isPublishing: boolean;
  /**
   * Why Publish cannot run this moment (unsaved edits that would ship the
   * previous source). Present ⇒ the button is disabled and carries this as its
   * tooltip, because a publish that silently does nothing reads as broken.
   */
  disabledHint?: string;
  label?: string;
}

/**
 * One line for the whole check run. Completed runs expand into every check;
 * failed runs lead with the first failure and its technical details.
 *
 * A real domain runs ten or more gates, so enumerating them by default turns
 * the most common state — "everything passed" — into a wall the reader has to
 * scan to learn nothing. Instead the strip answers the two questions a run
 * actually raises: how far along is it, and did anything go wrong. The full
 * list is one click away for when the answer is "yes".
 *
 * While a run is in flight the line names the check currently executing, so
 * the strip advances through the run rather than sitting on a spinner.
 */
export const GateProgressStrip = <GateKey extends string>({
  gates,
  config,
  heading,
  isPublishing,
  horizontalMargin = 2,
  topMargin = 2,
  bottomMargin = 0,
  repair,
  priorAttempts = EMPTY_PRIOR_ATTEMPTS,
  abandonedReason = null,
  describesLiveVersion = false,
  onRunChecksAgain,
  runChecksAgainDisabled = false,
  onAskAiFix,
  onSelectGate,
  publish,
  operation,
}: GateProgressStripProps<GateKey>) => {
  const summary = summarizeGates(
    gates,
    config,
    isPublishing,
    Boolean(repair),
    abandonedReason !== null
  );
  // The failure the tracked run is showing right now. A run in flight has no
  // verdict yet, so its failed rows are not history until it settles.
  const liveFailure =
    summary.phase === "failed" || summary.phase === "repairing"
      ? summary.failed[0]
      : undefined;
  const failureHistory = groupGateFailures(
    [
      ...(summary.phase === "passed"
        ? []
        : priorAttempts.flatMap(({ attempt, gates: attemptGates }) =>
            attemptGates[0] !== undefined
              ? [{ attempt, gate: attemptGates[0], live: false }]
              : []
          )),
      ...(liveFailure
        ? [
            {
              attempt: liveAttemptNumber(repair ?? null, priorAttempts),
              gate: liveFailure,
              live: true,
            },
          ]
        : []),
    ],
    config
  );
  const hasFailureHistory = failureHistory.length > 0;
  const [expanded, setExpanded] = useImmer(!operation && hasFailureHistory);
  const [historyExpanded, setHistoryExpanded] = useImmer(false);
  const [reviewExpanded, setReviewExpanded] = useImmer(false);
  const operationStatus = operation ? OPERATION_STATUS[operation.phase] : null;
  const operationBusy =
    operation?.phase === "checking" || operation?.phase === "publishing";
  const previousFailures = operation
    ? groupGateFailures(
        priorAttempts.flatMap(({ attempt, gates: previousGates }) =>
          previousGates
            .filter(
              (gate) => gate.status === "failed" || gate.status === "blocked"
            )
            .map((gate) => ({ attempt, gate, live: false }))
        ),
        config
      )
    : [];

  // A run that already refused a publish opens by itself: the reader should
  // not have to find "View checks" to learn that one of these attempts was
  // rejected, and in the running/passed windows nothing else says so.
  useEffect(() => {
    if (!operation) {
      setExpanded(hasFailureHistory);
    }
  }, [hasFailureHistory, operation, setExpanded]);

  useEffect(() => {
    if (operationBusy) {
      setReviewExpanded(false);
    }
  }, [operationBusy, setReviewExpanded]);

  const { headlineIcon, headline, detail } = operationStatus
    ? {
        headlineIcon: GATE_STATUS_STYLE[operationStatus.style],
        headline:
          operation?.checksOnly && operation.phase === "failed"
            ? "Checks need attention"
            : operationStatus.headline,
        detail: operation?.detail,
      }
    : describeRun(
        summary,
        config,
        repair ?? null,
        abandonedReason,
        describesLiveVersion,
        publish !== undefined
      );
  const { Icon: phaseIcon, fg: phaseFg, spin } = headlineIcon;
  const tone = resolveStripTone(
    operationStatus?.phase ?? summary.phase,
    publish !== undefined
  );
  const toneColors = tone === "plain" ? null : GATE_TONE_COLORS[tone];
  // A tinted bar dresses its glyph to match the border; a plain one keeps the
  // phase's own quieter colour.
  const Icon = toneColors?.Icon ?? phaseIcon;
  const fg = toneColors?.icon ?? phaseFg;
  // The bar's actions carry the same colour as its border, so a red-edged
  // failure does not sit above a blue button telling a different story.
  const actionColor = operation
    ? "secondary"
    : (toneColors?.button ?? "secondary");
  // Publish keeps the default primary when the run is untinted (mid-flight,
  // or stopped) rather than falling back to the quiet secondary the recovery
  // actions use — it is still the page's primary action.
  const publishColor = operation
    ? "primary"
    : (toneColors?.button ?? "primary");
  const failedGate =
    !operation && summary.phase === "failed" ? summary.failed[0] : undefined;
  const HeaderIcon = failedGate ? X : Icon;
  // The live run failing once, one way, is the plain case: the header already
  // names the check, so the body is just the message. Anything more — a
  // refusal the header does not name, or several attempts — is a history the
  // reader has to be able to tell apart by attempt.
  const singleFailure =
    failureHistory.length === 1 &&
    failureHistory[0].live &&
    failureHistory[0].attempts.length === 1
      ? failureHistory[0]
      : null;
  let headerLayout: SxProps<Theme> = { minHeight: 42, px: 2.5, py: 1 };
  if (operation) {
    headerLayout = {
      ...headerLayout,
      flexWrap: "wrap" as const,
      gap: 1,
    };
  } else if (failedGate) {
    headerLayout = { minHeight: 52, px: 3.5, py: 2.5 };
  }

  return (
    <IgPaper
      component="section"
      role="region"
      aria-label={heading}
      sx={{
        mt: topMargin,
        mb: bottomMargin,
        mx: horizontalMargin,
        p: 0,
        ...(toneColors !== null && {
          border: `1px solid ${toneColors.border}`,
        }),
        "@keyframes ig-gate-spin": {
          from: { transform: "rotate(0deg)" },
          to: { transform: "rotate(360deg)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          svg: { animation: "none !important", transition: "none !important" },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={failedGate ? 2 : 1.25}
        sx={headerLayout}
      >
        <HeaderIcon
          size={failedGate ? 14 : 16}
          color={fg}
          aria-hidden
          style={
            spin
              ? { animation: "ig-gate-spin 0.9s linear infinite" }
              : undefined
          }
        />
        <IgTypography
          variant="body2"
          role="status"
          aria-live="polite"
          sx={{
            color: IgCoreTextColors.primary,
            ...(operation ? { minWidth: 0 } : { flexShrink: 0 }),
            fontWeight: 600,
          }}
        >
          {headline}
        </IgTypography>
        {detail ? (
          <IgTypography
            variant="body2"
            sx={{
              color: IgCoreTextColors.secondary,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: operation ? "normal" : "nowrap",
            }}
          >
            {detail}
          </IgTypography>
        ) : null}

        <Box sx={{ flex: 1 }} />

        {/* One group, so Publish is always the rightmost control whether the
            run failed or passed — the reader never hunts for the same button
            in two places. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          role="group"
          aria-label={failedGate ? "Failed check actions" : "Check actions"}
          sx={{
            flexShrink: 0,
            ...(operation && { flexWrap: "wrap", gap: 1, ml: "auto" }),
          }}
        >
          {failedGate ? (
            <>
              {onRunChecksAgain ? (
                <IgButton
                  size="extraSmall"
                  variant="text"
                  color={actionColor}
                  onClick={onRunChecksAgain}
                  disabled={runChecksAgainDisabled}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Run checks again
                </IgButton>
              ) : null}
              {onAskAiFix ? (
                <IgButton
                  size="extraSmall"
                  variant="outlined"
                  color={actionColor}
                  startIcon={<Wrench size={16} />}
                  onClick={onAskAiFix}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Ask Atlas to fix
                </IgButton>
              ) : null}
              <IconButton
                aria-label={
                  expanded ? "Collapse check details" : "Expand check details"
                }
                size="small"
                onClick={() => setExpanded((open) => !open)}
                sx={{ height: 32, width: 32 }}
              >
                <ChevronDown
                  size={16}
                  style={{
                    transform: expanded ? "rotate(180deg)" : undefined,
                    transition: "transform 120ms ease",
                  }}
                />
              </IconButton>
            </>
          ) : (
            <IgButton
              size="mini"
              variant="text"
              color={actionColor}
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              endIcon={
                <ChevronDown
                  size={14}
                  style={{
                    transform: expanded ? "rotate(180deg)" : undefined,
                    transition: "transform 120ms ease",
                  }}
                />
              }
            >
              {expanded ? "Hide checks" : "View checks"}
            </IgButton>
          )}
          {operation?.phase === "review" ? (
            <IgButton
              size="small"
              variant="contained"
              onClick={() => setReviewExpanded((open) => !open)}
              aria-expanded={reviewExpanded}
            >
              {reviewExpanded ? "Hide change" : "Review change"}
            </IgButton>
          ) : null}
          {publish &&
          operation?.phase !== "review" &&
          operation?.phase !== "published" ? (
            <IgTooltip title={publish.disabledHint ?? ""}>
              {/* Stays mounted while publishing rather than swapping out, so
                  the bar does not reflow mid-publish. */}
              <span>
                <IgButton
                  // Named explicitly because the loading state swaps the label
                  // for a spinner, and a button with no accessible name is
                  // unreachable exactly while the publish is in flight.
                  aria-label={publish.label ?? "Publish"}
                  size="small"
                  variant="contained"
                  color={publishColor}
                  disabled={
                    operationBusy ||
                    publish.isPublishing ||
                    publish.disabledHint !== undefined
                  }
                  loading={!operation && publish.isPublishing}
                  // Called with no arguments on purpose: the publish handler
                  // takes optional notes/title, so handing it straight to
                  // onClick would pass the click event as `notes`.
                  onClick={() => publish.onPublish()}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {publish.label ?? "Publish"}
                </IgButton>
              </span>
            </IgTooltip>
          ) : null}
        </Stack>
      </Stack>

      {operation?.content &&
      (operation.phase === "failed" ||
        (operation.phase === "review" && reviewExpanded)) ? (
        <Stack sx={{ px: 2.5, pb: 2.5 }}>{operation.content}</Stack>
      ) : null}

      {!operation && singleFailure ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3.5, pb: 3.5 }}>
            <FailureBody
              text={singleFailure.detail.body ?? singleFailure.detail.summary}
              testId={failureTestId(singleFailure)}
            />
          </Box>
        </Collapse>
      ) : !operation && hasFailureHistory ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack
            component="ul"
            data-testid="gate-failure-history"
            sx={{
              borderTop: `1px solid ${IgBorderColors.core.default}`,
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {failureHistory.map((group, index) => (
              <FailureGroupRow
                key={`${group.gate.gateKey}:${group.attempts[0]}`}
                group={group}
                config={config}
                defaultOpen={index === 0}
              />
            ))}
          </Stack>
        </Collapse>
      ) : null}

      {/* The check list is progress, not a verdict: it belongs beside a run
          that is moving or done, and only distracts from a failure the reader
          is trying to read. */}
      {expanded && (operation || !liveFailure) ? (
        <Stack
          component="ul"
          data-testid="gate-progress-list"
          sx={{
            borderTop: `1px solid ${IgBorderColors.core.default}`,
            listStyle: "none",
            m: 0,
            maxHeight: 260,
            overflowY: "auto",
            p: 0,
          }}
        >
          {gates.map((gate) => (
            <GateRow
              key={gate.gateKey}
              gate={gate}
              config={config}
              onSelectGate={onSelectGate}
            />
          ))}
        </Stack>
      ) : null}
      {operation && expanded && previousFailures.length > 0 ? (
        <Stack
          sx={{
            borderTop: `1px solid ${IgBorderColors.core.default}`,
            px: 2.5,
            py: 1,
          }}
        >
          <IgButton
            size="mini"
            variant="text"
            color="secondary"
            sx={{ alignSelf: "flex-start" }}
            aria-expanded={historyExpanded}
            onClick={() => setHistoryExpanded((open) => !open)}
            endIcon={
              <ChevronDown
                size={14}
                style={{
                  transform: historyExpanded ? "rotate(180deg)" : undefined,
                }}
              />
            }
          >
            Previous attempts
          </IgButton>
          {historyExpanded ? (
            <Stack
              component="ul"
              data-testid="gate-failure-history"
              sx={{ listStyle: "none", m: 0, p: 0 }}
            >
              {previousFailures.map((group) => (
                <FailureGroupRow
                  key={`${group.gate.gateKey}:${group.attempts[0]}`}
                  group={group}
                  config={config}
                  defaultOpen={false}
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </IgPaper>
  );
};

const GateRow = <GateKey extends string>({
  gate,
  config,
  onSelectGate,
}: {
  gate: WorkspaceGateResult<GateKey>;
  config: WorkspaceGatesConfig<GateKey>;
  onSelectGate?: (gateKey: GateKey) => void;
}) => {
  const [expanded, setExpanded] = useImmer(false);
  const style = resolveGateStyle(gate, config);
  const { Icon, fg, spin } = style;
  const { summary, body } = describeGateDetail(gate, config);

  // A row with more to say opens in place — the failure the reader is chasing
  // is right here, and sending them to another tab to read it loses the run
  // they were looking at. A row with nothing more stays a shortcut to the
  // gate's own detail, and only when there is somewhere for it to go.
  const canNavigate = Boolean(onSelectGate);
  const interactive = body !== null || canNavigate;
  const activate = () => {
    if (body !== null) {
      setExpanded((open) => !open);
      return;
    }
    onSelectGate?.(gate.gateKey);
  };

  return (
    <Box component="li">
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={interactive ? activate : undefined}
        {...(interactive
          ? {
              role: "button",
              tabIndex: 0,
              ...(body !== null ? { "aria-expanded": expanded } : {}),
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  activate();
                }
              },
            }
          : {})}
        sx={{
          cursor: interactive ? "pointer" : "default",
          minHeight: 34,
          px: 2.5,
          py: 0.75,
          "&:hover": interactive
            ? { bgcolor: IgCoreBackgroundColors.baseLightMid }
            : undefined,
        }}
      >
        <Icon
          size={14}
          color={fg}
          aria-hidden
          style={
            spin
              ? { animation: "ig-gate-spin 0.9s linear infinite" }
              : undefined
          }
        />
        <IgTypography
          variant="body2"
          sx={{
            color: IgCoreTextColors.primary,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {gateLabel(gate.gateKey, config)}
        </IgTypography>
        <IgTypography
          variant="body2"
          sx={{
            color: IgCoreTextColors.tertiary,
            flexShrink: 0,
            maxWidth: "45%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary}
        </IgTypography>
        {body !== null && canNavigate ? (
          // On the row rather than inside the expanded detail: the row's own
          // click is taken by the expand, so this is the only way to reach the
          // gate's tab, and burying it under a disclosure hides the exit.
          <IgButton
            size="mini"
            variant="text"
            color="secondary"
            aria-label={`Open ${gateLabel(gate.gateKey, config)}`}
            onClick={(event) => {
              event.stopPropagation();
              onSelectGate?.(gate.gateKey);
            }}
            sx={{ flexShrink: 0 }}
          >
            Open
          </IgButton>
        ) : null}
        {body !== null ? (
          <ChevronDown
            size={14}
            color={IgCoreTextColors.tertiary}
            aria-hidden
            style={{
              flexShrink: 0,
              transform: expanded ? "rotate(180deg)" : undefined,
              transition: "transform 120ms ease",
            }}
          />
        ) : null}
      </Stack>

      {body !== null ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack
            spacing={1}
            sx={{
              bgcolor: IgCoreBackgroundColors.baseLightMid,
              borderTop: `1px solid ${IgBorderColors.core.default}`,
              px: 2.5,
              py: 1.25,
            }}
          >
            <FailureBody text={body} testId={`gate-detail-${gate.gateKey}`} />
          </Stack>
        </Collapse>
      ) : null}
    </Box>
  );
};

/**
 * Which attempt the tracked run's own failure is. The repair loop numbers its
 * attempts, so while it owns the run its counter is authoritative; once it has
 * let go, the tracked job is the one after the last attempt it refused.
 */
const liveAttemptNumber = (
  repair: { attempt: number } | null,
  priorAttempts: readonly { attempt: number }[]
): number =>
  repair
    ? repair.attempt
    : priorAttempts.reduce(
        (last, prior) => Math.max(last, prior.attempt + 1),
        0
      );

/**
 * The live failure keeps the id the plain failed run always had; a refusal
 * the loop moved past is addressed by the last attempt it refused.
 */
const failureTestId = <GateKey extends string>(
  group: GateFailureGroup<GateKey>
): string =>
  group.live
    ? `gate-failure-detail-${group.gate.gateKey}`
    : `gate-attempt-${group.attempts[group.attempts.length - 1]}`;

const FailureBody = ({ text, testId }: { text: string; testId: string }) => {
  const firstBreak = text.search(/[\r\n]/);
  const summary = firstBreak < 0 ? text : text.slice(0, firstBreak);
  const remainder = firstBreak < 0 ? "" : text.slice(firstBreak).trimStart();
  return (
    <Box
      component={remainder ? "details" : "div"}
      data-testid={testId}
      sx={{
        bgcolor: IgCoreBackgroundColors.interactive.neutral.default,
        border: `1px solid ${IgBorderColors.core.default}`,
        borderRadius: 1.5,
        color: IgCoreTextColors.primary,
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 11.5,
        lineHeight: "18px",
        m: 0,
        "&[open] > summary svg": { transform: "rotate(180deg)" },
      }}
    >
      <Box
        component={remainder ? "summary" : "div"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 2,
          listStyle: "none",
          cursor: remainder ? "pointer" : undefined,
          "&::-webkit-details-marker": { display: "none" },
          "&:focus-visible": {
            outline: `2px solid ${IgCoreTextColors.accent}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {summary}
        </Box>
        {remainder ? (
          <ChevronDown size={14} aria-hidden style={{ flexShrink: 0 }} />
        ) : null}
      </Box>
      {remainder ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            px: 3,
            pb: 2,
            font: "inherit",
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {remainder}
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * One distinct refusal, named by every attempt it refused. The newest one
 * starts open — it is what the reader came for — and an earlier, different
 * one starts closed so it reads as context rather than a second live problem.
 */
const FailureGroupRow = <GateKey extends string>({
  group,
  config,
  defaultOpen,
}: {
  group: GateFailureGroup<GateKey>;
  config: WorkspaceGatesConfig<GateKey>;
  defaultOpen: boolean;
}) => {
  const [expanded, setExpanded] = useImmer(defaultOpen);
  const { Icon, fg } = resolveGateStyle(group.gate, config);
  const text = group.detail.body ?? group.detail.summary;

  return (
    <Box component="li">
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((open) => !open);
          }
        }}
        sx={{
          cursor: "pointer",
          minHeight: 34,
          px: 2.5,
          py: 0.75,
          "&:hover": { bgcolor: IgCoreBackgroundColors.baseLightMid },
        }}
      >
        <Icon size={14} color={fg} aria-hidden />
        <IgTypography
          variant="body2"
          sx={{
            color: IgCoreTextColors.primary,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {`${attemptsPhrase(group.attempts)} — ${gateSpec(config, group.gate.gateKey).shortLabel} failed`}
        </IgTypography>
        <IgTypography
          variant="body2"
          sx={{
            color: IgCoreTextColors.tertiary,
            flexShrink: 0,
            maxWidth: "45%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {group.detail.summary}
        </IgTypography>
        <ChevronDown
          size={14}
          color={IgCoreTextColors.tertiary}
          aria-hidden
          style={{
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : undefined,
            transition: "transform 120ms ease",
          }}
        />
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2.5, pb: 2 }}>
          <FailureBody text={text} testId={failureTestId(group)} />
        </Box>
      </Collapse>
    </Box>
  );
};

/** The headline the strip leads with, per run phase. */
const describeRun = <GateKey extends string>(
  summary: GateRunSummary<GateKey>,
  config: WorkspaceGatesConfig<GateKey>,
  repair: { attempt: number; maxAttempts: number; authorName: string } | null,
  abandonedReason: string | null,
  describesLiveVersion: boolean,
  /**
   * There is a saved draft ahead of what serves. The settled phases say so
   * outright rather than leaving "ready to publish" to imply it: a green bar
   * that does not mention the storefront reads as "you are done".
   */
  hasUnpublishedDraft: boolean
) => {
  const { phase, total, settled, current, failed, needsReview } = summary;

  if (phase === "abandoned") {
    // Deliberately reports what ran rather than what passed: the run is real
    // history, but the entity moved on without it, so any pass/fail verdict
    // drawn from it would contradict the state shown beside the strip.
    return {
      headlineIcon: GATE_STATUS_STYLE.pending,
      headline: `Checks stopped · ${settled} of ${total} ran`,
      detail: abandonedReason ? `— ${abandonedReason}` : undefined,
    };
  }

  if (phase === "repairing") {
    // The last iteration's failures are on screen but being worked on. The
    // spinner (running style) is deliberate: something IS happening — the
    // model is writing a fix — even though no gate row is in flight.
    return {
      headlineIcon: GATE_STATUS_STYLE.running,
      headline: repair
        ? `${repair.authorName} is fixing this · attempt ${Math.min(repair.attempt + 1, repair.maxAttempts)} of ${repair.maxAttempts}`
        : "The repair loop is fixing this",
      detail:
        failed[0] !== undefined
          ? `— ${gateLabel(failed[0].gateKey, config)}`
          : undefined,
    };
  }

  if (phase === "running") {
    // `settled + 1` names the check being worked on, not the count finished —
    // "1 of 12" on the first check reads better than "0 of 12".
    const position = Math.min(settled + 1, total);
    const firstFailed = failed[0];
    const firstFailedDetail = firstFailed
      ? describeGateDetail(firstFailed, config)
      : null;
    return {
      headlineIcon: GATE_STATUS_STYLE.running,
      headline: repair
        ? `Re-running checks · attempt ${Math.max(repair.attempt, 1)} of ${repair.maxAttempts}`
        : `Running checks · ${position} of ${total}`,
      detail:
        firstFailed && firstFailedDetail
          ? `— ${gateSpec(config, firstFailed.gateKey).shortLabel}: ${firstFailedDetail.summary}`
          : current
            ? `— ${gateLabel(current.gateKey, config)}`
            : undefined,
    };
  }

  if (phase === "failed") {
    const first = failed[0];
    const position = first
      ? config.order.findIndex((gate) => gate.key === first.gateKey) + 1
      : 0;
    const firstDetail = first ? describeGateDetail(first, config) : null;
    return {
      headlineIcon: GATE_STATUS_STYLE.failed,
      headline: first
        ? `${gateLabel(first.gateKey, config)} failed · ${position} of ${total}`
        : `${failed.length} of ${total} checks failed`,
      detail: firstDetail ? `— ${firstDetail.summary}` : undefined,
    };
  }

  if (phase === "needs_review") {
    return {
      headlineIcon: GATE_STATUS_STYLE.warning,
      headline: `${needsReview.length} of ${total} checks need review`,
      detail: hasUnpublishedDraft
        ? "— not live yet, publishing is not blocked"
        : "— publishing is not blocked",
    };
  }

  if (phase === "passed") {
    return {
      headlineIcon: GATE_STATUS_STYLE.passed,
      headline: `All ${total} checks passed`,
      // Beside a rule this run already shipped, "ready to publish" invites an
      // action that has happened; report the run as history instead.
      detail: describesLiveVersion
        ? "— this version is live"
        : hasUnpublishedDraft
          ? "— not live yet, publish to ship it"
          : "— ready to publish",
    };
  }

  return {
    headlineIcon: GATE_STATUS_STYLE.pending,
    headline: "Checks haven't run yet",
    detail: hasUnpublishedDraft
      ? "— not live yet, they run when you publish"
      : "— they run when you publish",
  };
};
